import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { Revenue } from "../../types";

interface Props {
  onSubmit: (formData: any) => void;
  data?: Array<Revenue>;
}

const RevenueFields = ({ onSubmit, data }: Props) => {
  const { register, control, handleSubmit, reset } = useForm();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "revenues",
  });
  const { formatMessage: f } = useIntl();

  useEffect(() => {
    if (data) {
      reset({ revenues: data });
    }
  }, [data, reset]);

  const now = new Date().getUTCFullYear();
  const range = (start: number, stop: number, step: number) =>
    Array.from(
      { length: (stop - start) / step + 1 },
      (_, i) => start + i * step
    );
  const years = range(now + 10, now - 10, -1);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col">
        <div className="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <div className="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="p-4 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {f({ id: "year" })}
                  </th>
                  <th className="p-4 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {f({ id: "target" })}
                  </th>
                  <th className="p-4 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                    {f({ id: "actual" })}
                  </th>
                  <th className="p-4 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {fields.map((item: any, index) => (
                  <tr key={index}>
                    <td className="p-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium text-gray-900">
                      <select
                        // @ts-ignore
                        {...register(`revenues.${index}.year`, {
                          required: true,
                        })}
                        defaultValue={item.year}
                        className="mt-1 block w-24 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      >
                        {years.map((year: number) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium text-gray-900">
                      <input
                        type="text"
                        // @ts-ignore
                        {...register(`revenues.${index}.target`, {
                          required: true,
                        })}
                        defaultValue={item.target}
                        className="w-full mt-1 focus:ring-orange-500 focus:border-orange-500 block shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </td>
                    <td className="p-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium text-gray-900">
                      <input
                        type="text"
                        // @ts-ignore
                        {...register(`revenues.${index}.actual`, {
                          required: true,
                        })}
                        defaultValue={item.actual}
                        className="w-full mt-1 focus:ring-orange-500 focus:border-orange-500 block shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </td>
                    <td className="p-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium text-gray-900">
                      <button
                        className="inline-flex items-center"
                        type="button"
                        onClick={() => remove(index)}
                      >
                        <svg
                          className="h-4 w-4 mr-2 text-gray-300"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        <span className="text-gray text-sm">
                          {f({ id: "actions.delete" })}
                        </span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="p-4 flex space-x-4">
        <span className="hidden sm:block shadow-sm rounded-md">
          <button
            type="button"
            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-500 focus:outline-none focus:shadow-outline-gray focus:border-gray-700 active:bg-gray-700 transition duration-150 ease-in-out"
            onClick={() =>
              append({
                year: new Date().getUTCFullYear(),
                target: 0,
                actual: 0,
              })
            }
          >
            <svg
              className="-ml-1 mr-2 h-5 w-5 text-gray-300"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            {f({ id: "actions.add" })}
          </button>
        </span>
        <span className="hidden sm:block shadow-sm rounded-md">
          <button
            type="submit"
            className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs leading-4 font-medium rounded-md text-white bg-coral-500 hover:bg-coral-400 focus:outline-none focus:shadow-outline-indigo focus:border-orange-600 active:bg-coral-600 transition duration-150 ease-in-out"
          >
            <svg
              className="-ml-1 mr-2 h-5 w-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            {f({ id: "actions.save" })}
          </button>
        </span>
      </div>
    </form>
  );
};

export default RevenueFields;
